import { useState } from 'preact/hooks';

import type { CheckSession } from '../check-session';
import { CameraScanner } from '../components/CameraScanner';
import { Numpad } from '../components/Numpad';
import { ResultOverlay } from '../components/ResultOverlay';
import type { Candidate, Station } from '../types';

interface Props {
  session: CheckSession;
  station: Station;
  /** Noch nicht hochgeladene, offline entschiedene Prüfungen. */
  pending?: number;
  online?: boolean;
  onActivity: () => void;
  onLock: () => void;
}

/**
 * Zwei Wege zur Person, mehr nicht: die Kamera und das Nummernfeld.
 *
 * Die Namenssuche gab es bis zum 02.09.2026 als dritten Reiter. Sie ist auf
 * Wunsch des Auftraggebers entfallen. Die Entscheidung passt zur
 * Pflicht-Jamaat-ID: wer eine Karte hat, hat eine Nummer, und eine
 * Namenssuche am Eingang konnte nie mehr als GELB liefern, also ohnehin nur
 * eine Rückfrage. Der Server kann sie weiterhin (`mode: 'name'`), das Gerät
 * schickt sie nicht mehr.
 */
/**
 * Fehlersuche am Eingang, eingeschaltet mit `?diag=1` in der Adresse.
 *
 * Welcher Erkenner laeuft und mit welcher Aufloesung, sieht man dem Geraet
 * sonst nicht an, und eine Entwicklerkonsole gibt es auf einem iPhone in
 * der Hand nicht. Ohne den Schalter bleibt die Anzeige weg: am Eingang
 * gehoert nichts auf den Bildschirm, das niemand braucht.
 */
function diagnoseAn(): boolean {
  if (typeof window === 'undefined') return false;
  return new URLSearchParams(window.location.search).get('diag') === '1';
}

export function CheckView({ session, station, pending = 0, online = true, onActivity, onLock }: Props) {
  const [digits, setDigits] = useState('');
  const [diagnose, setDiagnose] = useState<string | null>(null);
  const phase = session.phase.value;

  const busy = phase.kind === 'pending';

  async function submitId(): Promise<void> {
    onActivity();
    await session.submit('id', digits);
    setDigits('');
  }

  async function confirm(candidate: Candidate): Promise<void> {
    onActivity();
    await session.confirm(candidate);
    setDigits('');
  }

  function dismiss(): void {
    onActivity();
    session.dismiss();
    setDigits('');
  }

  async function retry(): Promise<void> {
    onActivity();
    // Mit der Art wiederholen, in der es hereinkam: ein gescannter Code darf
    // im Protokoll nicht als Zifferneingabe erscheinen.
    if (phase.kind === 'offline') await session.submit(phase.mode, phase.query);
  }

  return (
    <div class="screen" onPointerDown={onActivity}>
      <header class="topbar">
        <span class="topbar__area">{station.area}</span>
        <span class="topbar__station">{station.name}</span>
        {/* Der Betrieb soll sehen, dass etwas aussteht. Nicht erst nach dem
            Event beim Abgleich der Zahlen. */}
        {!online && <span class="topbar__state" title="Keine Verbindung">offline</span>}
        {pending > 0 && (
          <span class="topbar__state" title="Noch nicht hochgeladen">{pending} ausstehend</span>
        )}
        {diagnose && <span class="topbar__state">{diagnose}</span>}
        <button class="topbar__lock" onClick={onLock} aria-label="Sperren">
          🔒
        </button>
      </header>

      {/* Der Scanner ist additiv: schlägt die Kamera fehl, deckt die
          Zifferneingabe die Anforderung weiterhin vollständig ab. */}
      <CameraScanner
        paused={phase.kind !== 'idle'}
        onCode={(raw) => {
          onActivity();
          void session.submit('scan', raw);
        }}
        onDiagnose={
          diagnoseAn()
            ? (d) => setDiagnose(`${d.engine} ${d.width}×${d.height}`)
            : undefined
        }
      />

      <section class="input-area">
        <output class="display" aria-label="Eingegebene Jamaat-ID">
          {digits || '—'}
        </output>
        <Numpad value={digits} onChange={setDigits} onSubmit={submitId} disabled={busy} />
      </section>

      <ResultOverlay phase={phase} onDismiss={dismiss} onConfirm={confirm} onRetry={retry} />
    </div>
  );
}
